import type { ReactNode } from 'react';
import { FaCheckCircle, FaExclamationCircle, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';

export type CalloutVariant = 'info' | 'warning' | 'error' | 'success';

const VARIANT_CLASSES: Record<CalloutVariant, { container: string; icon: string; title: string; body: string }> = {
    error: {
        body: 'text-red-300/90',
        container: 'border-red-900 bg-red-950/30',
        icon: 'text-red-400',
        title: 'text-red-300'
    },
    info: {
        body: 'text-blue-400/80',
        container: 'border-blue-900 bg-blue-950/30',
        icon: 'text-blue-400',
        title: 'text-blue-300'
    },
    success: {
        body: 'text-green-200/80',
        container: 'border-green-900 bg-green-950/30',
        icon: 'text-green-400',
        title: 'text-green-300'
    },
    warning: {
        body: 'text-amber-200/80',
        container: 'border-amber-900/60 bg-amber-950/30',
        icon: 'text-amber-400',
        title: 'text-amber-300'
    }
};

const DEFAULT_ICONS: Record<CalloutVariant, ReactNode> = {
    error: <FaExclamationCircle className="h-4 w-4" />,
    info: <FaInfoCircle className="h-4 w-4" />,
    success: <FaCheckCircle className="h-4 w-4" />,
    warning: <FaExclamationTriangle className="h-4 w-4" />
};

interface CalloutProps {
    variant: CalloutVariant;
    children: ReactNode;
    /** Bold first line above the body */
    title?: ReactNode;
    /** Overrides the variant's default icon; pass null to hide the icon */
    icon?: ReactNode | null;
    className?: string;
}

/**
 * Inline tinted callout box for contextual info, warnings, errors, and
 * confirmations inside forms and cards (the page-level dismissible banner
 * remains the Alert component).
 */
const Callout = ({ variant, children, title, icon, className = '' }: CalloutProps) => {
    const classes = VARIANT_CLASSES[variant];
    const resolvedIcon = icon === undefined ? DEFAULT_ICONS[variant] : icon;

    return (
        <div className={`flex items-start gap-2 rounded-md border p-3 ${classes.container} ${className}`}>
            {resolvedIcon !== null && <span className={`mt-0.5 shrink-0 ${classes.icon}`}>{resolvedIcon}</span>}
            <div className="min-w-0 flex-1 text-sm">
                {title && <p className={`font-medium ${classes.title}`}>{title}</p>}
                <div className={`${title ? 'mt-1 ' : ''}${classes.body}`}>{children}</div>
            </div>
        </div>
    );
};

export default Callout;
